import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdMailOutline,
  MdPhone,
  MdSmartphone,
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdPerson,
  MdBadge,
  MdHistory,
  MdPhotoCamera,
} from 'react-icons/md';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { getInitials } from '../../utils/attendanceFormatters';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import HrPortalAccessModal from './HrPortalAccessModal';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const TABS = [
  { id: 'personal', label: 'Personal' },
  { id: 'work', label: 'Work' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'resume', label: 'Resume' },
  { id: 'settings', label: 'Settings' },
];

const EMPTY = {
  name: '',
  identification_id: '',
  work_email: '',
  private_email: '',
  work_phone: '',
  mobile_phone: '',
  job_title: '',
  department_id: '',
  work_location: '',
  attendance_device_no: '',
  gender: '',
  birthday: '',
  marital: '',
  place_of_birth: '',
  passport_id: '',
  emergency_contact: '',
  emergency_phone: '',
  active: true,
  // Payroll (Odoo-style; sent when backend accepts)
  wage: '',
  wage_type: 'monthly',
  employee_type: 'employee',
  contract_type: '',
  pay_category: '',
  contract_reference: '',
  working_hours_label: 'Standard 40 hours/week',
  resource_calendar_id: '',
};

function Field({ label, children, className = '' }) {
  return (
    <div className={`ea-odoo-field ${className}`}>
      <label className="ea-odoo-field__label">{label}</label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section className="ea-odoo-group">
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </section>
  );
}

/** Odoo empty fields are often boolean `false`, not null/"". */
function odooText(val) {
  if (val === false || val == null || val === '') return '';
  if (Array.isArray(val)) return String(val[1] ?? val[0] ?? '');
  return String(val);
}

/** Many2one may be int, or `[id, "Name"]`, or `{ id, name }`. */
function odooId(val) {
  if (val === false || val == null || val === '') return '';
  if (Array.isArray(val)) return val[0] != null ? String(val[0]) : '';
  if (typeof val === 'object' && val.id != null) return String(val.id);
  return String(val);
}

function odooDate(val) {
  const raw = odooText(val);
  if (!raw) return '';
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  // DD/MM/YYYY
  const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return raw;
}

function odooImageSrc(emp, baseURL) {
  const url = odooText(emp.image_url || emp.avatar_url);
  if (url) {
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    if (url.startsWith('/')) return `${(baseURL || '').replace(/\/$/, '')}${url}`;
  }
  const b64 = emp.image_128 || emp.image_1920 || emp.image;
  if (typeof b64 === 'string' && b64.length > 50 && !b64.startsWith('http') && b64 !== 'false') {
    if (b64.startsWith('data:')) return b64;
    return `data:image/jpeg;base64,${b64}`;
  }
  return null;
}

/** Normalize any common HR get envelopes to a flat employee object. */
function unwrapEmployee(data) {
  if (!data || typeof data !== 'object') return null;
  const candidates = [
    data.employee,
    data.data?.employee,
    data.data,
    data.record,
    data.result,
  ];
  for (const c of candidates) {
    if (c && typeof c === 'object' && !Array.isArray(c) && (c.id != null || c.name)) {
      return c;
    }
  }
  if (data.id != null || data.name) return data;
  return null;
}

export default function HrEmployeeFormPage() {
  const { employeeId } = useParams();
  const isNew = !employeeId || employeeId === 'new';
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();

  const [tab, setTab] = useState('personal');
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({
    has_portal_access: false,
    user_login: '',
    department_name: '',
    image_url: null,
  });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [portalResult, setPortalResult] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const fileInputRef = useRef(null);

  const setField = (key, value) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearImageDraft = () => {
    setImagePreview(null);
    setImageBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (JPG, PNG, etc.).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      setImagePreview(dataUrl);
      setImageBase64(base64);
      setDirty(true);
      setError(null);
    };
    reader.onerror = () => setError('Could not read the image file.');
    reader.readAsDataURL(file);
  };

  const loadDepartments = useCallback(async () => {
    const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getDepartments(token)
    );
    if (data?.departments) setDepartments(data.departments);
  }, [ensureHrToken]);

  const loadEmployee = useCallback(async () => {
    if (isNew) {
      setForm(EMPTY);
      setMeta({
        has_portal_access: false,
        user_login: '',
        department_name: '',
        image_url: null,
      });
      clearImageDraft();
      setDirty(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getEmployee(employeeId, token)
    );
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    const emp = unwrapEmployee(data);
    if (!emp) {
      setError(
        'Employee get API returned no employee object. Backend must return result.employee with fields.'
      );
      setLoading(false);
      return;
    }

    const deptId = odooId(emp.department_id);
    const deptName =
      odooText(emp.department_name) ||
      (Array.isArray(emp.department_id) ? odooText(emp.department_id) : '');

    setForm({
      name: odooText(emp.name),
      identification_id:
        odooText(emp.employee_no) ||
        odooText(emp.identification_id) ||
        odooText(emp.barcode) ||
        '',
      work_email: odooText(emp.work_email) || odooText(emp.email) || '',
      private_email:
        odooText(emp.private_email) || odooText(emp.personal_email) || '',
      work_phone: odooText(emp.work_phone) || odooText(emp.phone) || '',
      mobile_phone:
        odooText(emp.mobile_phone) || odooText(emp.mobile) || odooText(emp.work_mobile) || '',
      job_title:
        odooText(emp.job_title) ||
        odooText(emp.job_id) ||
        odooText(emp.job_position) ||
        '',
      department_id: deptId,
      work_location:
        odooText(emp.work_location) ||
        odooText(emp.work_location_id) ||
        odooText(emp.location) ||
        '',
      attendance_device_no:
        odooText(emp.attendance_device_no) ||
        odooText(emp.attendance_device_id) ||
        odooText(emp.device_id_num) ||
        '',
      gender: odooText(emp.gender).toLowerCase(),
      birthday: odooDate(emp.birthday || emp.birth_date),
      marital: odooText(emp.marital || emp.marital_status).toLowerCase(),
      place_of_birth:
        odooText(emp.place_of_birth) || odooText(emp.place_of_birth_city) || '',
      passport_id: odooText(emp.passport_id) || odooText(emp.passport_no) || '',
      emergency_contact:
        odooText(emp.emergency_contact) || odooText(emp.emergency_contact_name) || '',
      emergency_phone:
        odooText(emp.emergency_phone) || odooText(emp.emergency_contact_phone) || '',
      active: emp.active !== false,
      wage: emp.wage != null && emp.wage !== false ? String(emp.wage) : '',
      wage_type: odooText(emp.wage_type) || 'monthly',
      employee_type: odooText(emp.employee_type) || 'employee',
      contract_type: odooText(emp.contract_type || emp.contract_type_id),
      pay_category: odooText(emp.pay_category),
      contract_reference: odooText(emp.contract_reference || emp.reference),
      working_hours_label:
        odooText(emp.working_hours_label) ||
        odooText(emp.resource_calendar_id) ||
        odooText(emp.resource_calendar_name) ||
        'Standard 40 hours/week',
      resource_calendar_id: odooId(emp.resource_calendar_id),
    });
    setMeta({
      has_portal_access: Boolean(emp.has_portal_access),
      user_login: odooText(emp.user_login) || odooText(emp.login) || '',
      department_name: deptName,
      image_url: odooImageSrc(emp, employeeAttendanceHrAPI.baseURL),
    });
    clearImageDraft();
    setDirty(false);
    setLoading(false);
  }, [ensureHrToken, employeeId, isNew]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadEmployee();
  }, [loadEmployee]);

  /**
   * Only send fields documented on the HR employees API.
   * Extra payroll UI fields must NOT be sent — Odoo rejects unknown keys.
   */
  const buildParams = () => {
    const str = (v) => {
      const t = String(v ?? '').trim();
      return t || undefined;
    };
    const params = {
      name: form.name.trim(),
      identification_id: str(form.identification_id),
      work_email: str(form.work_email),
      private_email: str(form.private_email),
      work_phone: str(form.work_phone),
      mobile_phone: str(form.mobile_phone),
      job_title: str(form.job_title),
      work_location: str(form.work_location),
      attendance_device_no: str(form.attendance_device_no),
      gender: str(form.gender),
      birthday: str(form.birthday),
      birth_date: str(form.birthday),
      marital: str(form.marital),
      marital_status: str(form.marital),
      place_of_birth: str(form.place_of_birth),
      passport_id: str(form.passport_id),
      emergency_contact: str(form.emergency_contact),
      emergency_phone: str(form.emergency_phone),
      active: form.active !== false,
    };

    if (form.department_id) {
      params.department_id = Number(form.department_id);
    }
    if (form.resource_calendar_id && /^\d+$/.test(String(form.resource_calendar_id).trim())) {
      params.resource_calendar_id = Number(form.resource_calendar_id);
    }
    if (imageBase64) {
      params.image_1920 = imageBase64;
    }
    if (!isNew) {
      params.employee_id = Number(employeeId);
    }

    // Drop undefined keys so JSON-RPC params stay clean
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) delete params[key];
    });
    return params;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      setError('Name is required.');
      setTab('personal');
      return;
    }

    setSaving(true);
    setError(null);
    const params = buildParams();

    const saveOnce = async (payload) =>
      fetchWithHrToken(ensureHrToken, (token) =>
        isNew
          ? employeeAttendanceHrAPI.createEmployee(payload, token)
          : employeeAttendanceHrAPI.updateEmployee(payload, token)
      );

    let { data, error: fetchError, result } = await saveOnce(params);

    // If photo field is not supported yet, retry without it so the rest still saves
    if (
      fetchError &&
      imageBase64 &&
      /image/i.test(fetchError + (result?.message || ''))
    ) {
      const retryParams = { ...params };
      delete retryParams.image_1920;
      const retry = await saveOnce(retryParams);
      data = retry.data;
      fetchError = retry.error;
      result = retry.result;
      if (!fetchError) {
        setError(
          'Employee saved, but the server rejected the photo field. Ask admin to allow image_1920 on the HR API.'
        );
      }
    }

    setSaving(false);

    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }

    const saved = data?.employee || data;
    clearImageDraft();
    setDirty(false);
    if (isNew && saved?.id) {
      navigate(`/bu-dashboard/hr/employees/${saved.id}`, { replace: true });
    } else if (isNew && !saved?.id) {
      // Create succeeded but no id in response — return to list
      navigate('/bu-dashboard/hr/employees');
    } else {
      loadEmployee();
    }
  };

  const handleDiscard = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    if (isNew) {
      navigate('/bu-dashboard/hr/employees');
      return;
    }
    clearImageDraft();
    loadEmployee();
  };

  const showDiscard = isNew || dirty;

  const resolvePhotoSrc = () => {
    if (imagePreview) return imagePreview;
    const raw = meta.image_url;
    if (!raw || raw === 'false') return null;
    if (String(raw).startsWith('http') || String(raw).startsWith('data:')) return raw;
    if (String(raw).startsWith('/')) {
      return `${employeeAttendanceHrAPI.baseURL}${raw}`;
    }
    return null;
  };

  const photoSrc = resolvePhotoSrc();

  const grantPortal = async () => {
    if (isNew) {
      setError('Save the employee first, then grant portal access.');
      return;
    }
    setPortalBusy(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.createPortalAccess(
        { employee_id: Number(employeeId), auto_generate_password: true },
        token
      )
    );
    setPortalBusy(false);
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }
    setPortalResult(data);
    loadEmployee();
  };

  const titleName = form.name.trim() || (isNew ? 'New' : 'Employee');
  const initials = getInitials(form.name || 'E');
  const statusLabel = form.active === false ? 'Archived' : 'Active';

  return (
    <div className="ea-odoo-form-page">
      <div className="ea-odoo-form-bar">
        <div className="ea-odoo-form-bar__left">
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {showDiscard && (
            <button
              type="button"
              className="ea-odoo-btn"
              onClick={handleDiscard}
              disabled={saving}
            >
              Discard
            </button>
          )}
          <nav className="ea-odoo-breadcrumb" aria-label="Breadcrumb">
            <Link to="/bu-dashboard/hr/employees">Employees</Link>
            <span className="ea-odoo-breadcrumb__sep">/</span>
            <span className="ea-odoo-breadcrumb__current">{titleName}</span>
          </nav>
        </div>
        <div className="ea-odoo-form-bar__right">
          {!isNew && (
            <div className="ea-odoo-smarts">
              <button
                type="button"
                className="ea-odoo-smart"
                onClick={grantPortal}
                disabled={portalBusy}
              >
                <MdBadge size={18} />
                <span>
                  <strong>{meta.has_portal_access ? 'Yes' : 'No'}</strong>
                  Portal
                </span>
              </button>
              <Link
                to={`/bu-dashboard/hr/attendance?employee_id=${employeeId}`}
                className="ea-odoo-smart"
              >
                <MdHistory size={18} />
                <span>
                  <strong>Lines</strong>
                  Attendance
                </span>
              </Link>
              <div className="ea-odoo-smart ea-odoo-smart--static">
                <MdPerson size={18} />
                <span>
                  <strong>{form.attendance_device_no || '—'}</strong>
                  Device
                </span>
              </div>
            </div>
          )}
          <div className="ea-odoo-pager ea-odoo-form-pager">
            <button
              type="button"
              className="ea-odoo-pager__btn"
              onClick={() => navigate('/bu-dashboard/hr/employees')}
              aria-label="Back to list"
            >
              <MdChevronLeft size={20} />
            </button>
            <span>{isNew ? 'New' : `#${employeeId}`}</span>
            <button type="button" className="ea-odoo-pager__btn" disabled aria-hidden>
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="ea-odoo-empty">Loading employee…</div>
      ) : (
        <form className="ea-odoo-sheet" onSubmit={handleSave}>
          <header className="ea-odoo-sheet__header">
            <button
              type="button"
              className="ea-odoo-sheet__photo"
              onClick={() => fileInputRef.current?.click()}
              aria-label={photoSrc ? 'Change employee photo' : 'Add employee photo'}
              title={photoSrc ? 'Click to change photo' : 'Click to add photo'}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="ea-odoo-sheet__photo-input"
                onChange={handleImagePick}
                tabIndex={-1}
              />
              {photoSrc ? (
                <img src={photoSrc} alt="" />
              ) : (
                <span className="ea-odoo-sheet__photo-placeholder">
                  <MdAdd size={28} />
                  <span>{initials}</span>
                </span>
              )}
              <span className="ea-odoo-sheet__photo-overlay">
                <MdPhotoCamera size={22} />
                <span>{photoSrc ? 'Edit' : 'Upload'}</span>
              </span>
            </button>

            <div className="ea-odoo-sheet__identity">
              <input
                className="ea-odoo-sheet__name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Employee's Name"
                required
                aria-label="Employee name"
              />
              <div className="ea-odoo-sheet__contacts">
                <label className="ea-odoo-sheet__contact">
                  <MdMailOutline aria-hidden />
                  <input
                    type="email"
                    value={form.work_email}
                    onChange={(e) => setField('work_email', e.target.value)}
                    placeholder="Email"
                  />
                </label>
                <label className="ea-odoo-sheet__contact">
                  <MdPhone aria-hidden />
                  <input
                    value={form.work_phone}
                    onChange={(e) => setField('work_phone', e.target.value)}
                    placeholder="Work Phone"
                  />
                </label>
                <label className="ea-odoo-sheet__contact">
                  <MdSmartphone aria-hidden />
                  <input
                    value={form.mobile_phone}
                    onChange={(e) => setField('mobile_phone', e.target.value)}
                    placeholder="Work Mobile"
                  />
                </label>
              </div>
            </div>

            <div className="ea-odoo-sheet__status">
              <span
                className={`ea-odoo-status-pill${
                  form.active === false ? ' ea-odoo-status-pill--off' : ''
                }`}
              >
                <span className="ea-odoo-status-pill__dot" />
                {statusLabel}
              </span>
            </div>
          </header>

          <div className="ea-odoo-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`ea-odoo-tabs__btn${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="ea-odoo-sheet__body" role="tabpanel">
            {tab === 'work' && (
              <div className="ea-odoo-cols">
                <div>
                  <Group title="Work">
                    <Field label="Department">
                      <select
                        value={form.department_id}
                        onChange={(e) => setField('department_id', e.target.value)}
                      >
                        <option value="">—</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Job Title">
                      <input
                        value={form.job_title}
                        onChange={(e) => setField('job_title', e.target.value)}
                      />
                    </Field>
                    <Field label="Employee No">
                      <input
                        value={form.identification_id}
                        onChange={(e) => setField('identification_id', e.target.value)}
                      />
                    </Field>
                  </Group>
                  <Group title="Location">
                    <Field label="Work Location">
                      <input
                        value={form.work_location}
                        onChange={(e) => setField('work_location', e.target.value)}
                      />
                    </Field>
                  </Group>
                </div>
                <div>
                  <Group title="Organization">
                    <p className="ea-odoo-muted">
                      {meta.department_name
                        ? `Department: ${meta.department_name}`
                        : 'Select a department to place this employee in the organization.'}
                    </p>
                  </Group>
                </div>
              </div>
            )}

            {tab === 'personal' && (
              <div className="ea-odoo-cols">
                <div>
                  <Group title="Private Contact">
                    <Field label="Private Email">
                      <input
                        type="email"
                        value={form.private_email}
                        onChange={(e) => setField('private_email', e.target.value)}
                      />
                    </Field>
                  </Group>
                  <Group title="Emergency">
                    <Field label="Contact">
                      <input
                        value={form.emergency_contact}
                        onChange={(e) => setField('emergency_contact', e.target.value)}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        value={form.emergency_phone}
                        onChange={(e) => setField('emergency_phone', e.target.value)}
                      />
                    </Field>
                  </Group>
                  <Group title="Citizenship">
                    <Field label="Identification No">
                      <input
                        value={form.identification_id}
                        onChange={(e) => setField('identification_id', e.target.value)}
                      />
                    </Field>
                    <Field label="Passport No">
                      <input
                        value={form.passport_id}
                        onChange={(e) => setField('passport_id', e.target.value)}
                      />
                    </Field>
                  </Group>
                </div>
                <div>
                  <Group title="Personal Information">
                    <Field label="Birthday">
                      <input
                        type="date"
                        value={form.birthday}
                        onChange={(e) => setField('birthday', e.target.value)}
                      />
                    </Field>
                    <Field label="Place of Birth">
                      <input
                        value={form.place_of_birth}
                        onChange={(e) => setField('place_of_birth', e.target.value)}
                      />
                    </Field>
                    <Field label="Gender">
                      <select
                        value={form.gender}
                        onChange={(e) => setField('gender', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>
                    <Field label="Marital Status">
                      <select
                        value={form.marital}
                        onChange={(e) => setField('marital', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="cohabitant">Legal Cohabitant</option>
                        <option value="widower">Widower</option>
                        <option value="divorced">Divorced</option>
                      </select>
                    </Field>
                  </Group>
                </div>
              </div>
            )}

            {tab === 'payroll' && (
              <div className="ea-odoo-cols">
                <div>
                  <Group title="Contract Overview">
                    <Field label="Contract">
                      <input value="Not Employed" readOnly />
                    </Field>
                    <Field label="Wage Type">
                      <select
                        value={form.wage_type}
                        onChange={(e) => setField('wage_type', e.target.value)}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="hourly">Hourly</option>
                      </select>
                    </Field>
                    <Field label="Wage">
                      <div className="ea-odoo-inline-input">
                        <span className="ea-odoo-prefix">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.wage}
                          onChange={(e) => setField('wage', e.target.value)}
                          placeholder="0.00"
                        />
                        <span className="ea-odoo-suffix">
                          / {form.wage_type === 'hourly' ? 'hour' : 'month'}
                        </span>
                      </div>
                    </Field>
                    <Field label="Employee Type">
                      <select
                        value={form.employee_type}
                        onChange={(e) => setField('employee_type', e.target.value)}
                      >
                        <option value="employee">Employee</option>
                        <option value="student">Student</option>
                        <option value="trainee">Trainee</option>
                        <option value="contractor">Contractor</option>
                        <option value="freelance">Freelancer</option>
                      </select>
                    </Field>
                    <Field label="Contract Type">
                      <input
                        value={form.contract_type}
                        onChange={(e) => setField('contract_type', e.target.value)}
                        placeholder="e.g. Permanent"
                      />
                    </Field>
                    <Field label="Pay Category">
                      <input
                        value={form.pay_category}
                        onChange={(e) => setField('pay_category', e.target.value)}
                      />
                    </Field>
                    <Field label="Reference">
                      <input
                        value={form.contract_reference}
                        onChange={(e) => setField('contract_reference', e.target.value)}
                      />
                    </Field>
                  </Group>
                  <Group title="Schedule">
                    <Field label="Work Entry Source">
                      <input value="Working Schedule" readOnly />
                    </Field>
                    <Field label="Working Hours">
                      <input
                        value={form.working_hours_label}
                        onChange={(e) => setField('working_hours_label', e.target.value)}
                        placeholder="Standard 40 hours/week"
                      />
                    </Field>
                    <Field label="Calendar ID">
                      <input
                        value={form.resource_calendar_id}
                        onChange={(e) => setField('resource_calendar_id', e.target.value)}
                        placeholder="resource_calendar_id"
                      />
                    </Field>
                  </Group>
                  <button type="button" className="ea-odoo-link-btn">
                    + Add Inputs
                  </button>
                </div>
                <div>
                  <Group title="Notes">
                    <p className="ea-odoo-muted">
                      Expected attendance hours follow the university rule of{' '}
                      <strong>10 hours per working day</strong> (Fridays excluded).
                    </p>
                  </Group>
                </div>
              </div>
            )}

            {tab === 'resume' && (
              <div className="ea-odoo-cols">
                <div>
                  <Group title="Resume">
                    <div className="ea-odoo-empty-panel">
                      <p>There are no resume lines on this employee.</p>
                      <button type="button" className="ea-odoo-btn">
                        Create a new entry
                      </button>
                    </div>
                  </Group>
                </div>
                <div>
                  <Group title="Skills">
                    <div className="ea-odoo-empty-panel">
                      <p>
                        You can add skills from our library to the list of the employee&apos;s
                        skills to showcase their expertise and proficiency.
                      </p>
                      <button type="button" className="ea-odoo-btn">
                        Pick a skill from the list
                      </button>
                    </div>
                  </Group>
                  <Group title="Certification">
                    <div className="ea-odoo-empty-panel">
                      <p>There are no certifications on this employee.</p>
                      <button type="button" className="ea-odoo-btn">
                        Create a new entry
                      </button>
                    </div>
                  </Group>
                </div>
              </div>
            )}

            {tab === 'settings' && (
              <div className="ea-odoo-cols">
                <div>
                  <Group title="User">
                    <Field label="Portal login">
                      <input
                        value={meta.user_login || form.work_email || ''}
                        readOnly
                        placeholder="Granted after portal access"
                      />
                    </Field>
                    <Field label="Portal access">
                      <div className="ea-odoo-inline">
                        <span>
                          {meta.has_portal_access ? 'Enabled' : 'Not granted'}
                        </span>
                        <button
                          type="button"
                          className="ea-odoo-btn ea-odoo-btn--primary ea-odoo-btn--sm"
                          onClick={grantPortal}
                          disabled={portalBusy || isNew}
                        >
                          {meta.has_portal_access ? 'Manage portal' : 'Grant portal'}
                        </button>
                      </div>
                    </Field>
                  </Group>
                </div>
                <div>
                  <Group title="Attendance / Point of Sale">
                    <Field label="Attendance Device ID">
                      <input
                        value={form.attendance_device_no}
                        onChange={(e) => setField('attendance_device_no', e.target.value)}
                        placeholder="Hikvision ID"
                      />
                    </Field>
                    <Field label="Active">
                      <label className="ea-odoo-check">
                        <input
                          type="checkbox"
                          checked={form.active !== false}
                          onChange={(e) => setField('active', e.target.checked)}
                        />
                        <span>Employee is active</span>
                      </label>
                    </Field>
                  </Group>
                </div>
              </div>
            )}
          </div>
        </form>
      )}

      {portalResult && (
        <HrPortalAccessModal result={portalResult} onClose={() => setPortalResult(null)} />
      )}
    </div>
  );
}
